import {
  Box,
  BoxProps,
  Button,
  Tooltip,
  forwardRef as chakraForwardRef,
} from "@chakra-ui/react";
import { ComponentProps, forwardRef, useEffect, useRef, useState } from "react";

const CONTAINER_NAME = "responsive-button-container";

type ResponsiveButtonProps = ComponentProps<typeof Button> & {
  hideLabelWhen: string;
  ["aria-label"]: string;
};

export const ResponsiveButton = forwardRef<
  HTMLButtonElement,
  ResponsiveButtonProps
>(function ResponsiveButton({ children, hideLabelWhen, ...props }, ref) {
  const labelRef = useRef<HTMLDivElement>(null);
  const [isLabelHidden, setIsLabelHidden] = useState(false);

  useEffect(() => {
    const label = labelRef.current;
    if (!label) return;

    const updateVisibility = () => {
      setIsLabelHidden(window.getComputedStyle(label).display === "none");
    };

    const observer = new ResizeObserver(updateVisibility);
    observer.observe(label);
    updateVisibility();

    return () => observer.disconnect();
  }, [hideLabelWhen]);
  return (
    <Tooltip label={children} isDisabled={!isLabelHidden}>
      <Button
        ref={ref}
        sx={{
          [`@container ${CONTAINER_NAME} (width ${hideLabelWhen})`]: {
            "& .chakra-button__icon": {
              marginInlineStart: 0,
              marginInlineEnd: 0,
            },
          },
        }}
        {...props}
      >
        <Box
          ref={labelRef}
          sx={{
            [`@container ${CONTAINER_NAME} (width ${hideLabelWhen})`]: {
              display: "none",
            },
          }}
        >
          {children}
        </Box>
      </Button>
    </Tooltip>
  );
});

type ResponsiveContainerProps = BoxProps;
export const ResponsiveContainer = chakraForwardRef<
  ResponsiveContainerProps,
  "div"
>(function ResponsiveContainer(props, ref) {
  return (
    <Box
      ref={ref}
      sx={{
        containerType: "inline-size",
        containerName: CONTAINER_NAME,
      }}
      {...props}
    />
  );
});
